import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, AlertTriangle, MessageSquare, Loader2 } from 'lucide-react';
import Notice from '../../components/common/Notice';
import { apiService } from '../../services/api.service';
import { API_ENDPOINTS } from '../../config/environment';
import { formatTL } from '../../utils/money';
import './SoftwareFeePage.css';

const MONTHS = [
    'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
    'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
];

const STATUS_META = {
    Draft: { label: 'Taslak', tone: 'draft', hint: 'Adet hesaplandı. İtirazınız yoksa fatura kesilecek.' },
    Disputed: { label: 'İtiraz edildi', tone: 'disputed', hint: 'Adet incelemede.' },
    Issued: { label: 'Fatura kesildi', tone: 'issued', hint: 'Vade içinde ödenmesi bekleniyor.' },
    Paid: { label: 'Ödendi', tone: 'paid', hint: 'Bu dönem kapandı.' },
    Overdue: { label: 'Vadesi geçti', tone: 'overdue', hint: 'Ödeme gecikti.' },
    Cancelled: { label: 'İptal', tone: 'cancelled', hint: 'Bu fatura iptal edildi.' },
};

/**
 * Kademeli yaptırımın filoya söylediği şey.
 *
 * "Kanalınız durdu" sürprizi olmamalı: hangi aşamada olduğu ve ne zaman ne olacağı
 * ödeme gecikmeye başladığı andan itibaren burada yazıyor.
 */
const STAGE_NOTICE = {
    Warned: {
        tone: 'warn',
        title: 'Ödeme vadesi geçti',
        body: 'Yedinci günden sonra hesabınız riskli işaretlenir, on beşinci günde entegrasyon kanalınız yeni sipariş almayı durdurur.',
    },
    AtRisk: {
        tone: 'warn',
        title: 'Hesabınız riskli işaretlendi',
        body: 'On beşinci günde entegrasyon kanalınız yeni sipariş almayı durduracak. Devam eden teslimatlar her durumda tamamlanır.',
    },
    Suspended: {
        tone: 'danger',
        title: 'Kanal durduruldu',
        body: 'Yeni sipariş kabul edilmiyor. Devam eden teslimatlar tamamlanıyor. Otuzuncu günde kanal tamamen kapanır.',
    },
    Terminated: {
        tone: 'danger',
        title: 'Kanal kapatıldı',
        body: 'Entegrasyon mağazalarınız pasife çekildi. Yeniden açmak için Hızlıyo ile iletişime geçin.',
    },
};

const formatDate = (value) => {
    if (!value) return '-';
    return new Date(value).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

/**
 * Yazılım bedeli.
 *
 * <b>Hakediş sekmesiyle karıştırılmamalı ve bu ekranda "faturalanacak" ibaresi hiç
 * geçmiyor:</b> orada siz alacaklısınız ve faturayı siz kesersiniz; burada Hızlıyo
 * teslim edilen paket başına yazılım bedeli alıyor ve faturayı size kesiyor. İkisini
 * aynı ekrana koymak muhasebenin ters yöne kayıt açmasına yol açardı.
 *
 * @param embedded Hakedişler ekranında sekme olarak açıldığında kendi başlığını çizmez.
 */
const SoftwareFeePage = ({ embedded = false }) => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [objecting, setObjecting] = useState(null);
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');

        try {
            const res = await apiService.get(API_ENDPOINTS.FLEET_PANEL.SOFTWARE_FEE_INVOICES);

            /* apiService zarfı ZATEN açıyor: dönen nesne { success, data, error }. Burada
               ikinci kez `res.data.success` aranıyordu; o alan hiçbir zaman dolmadığı için
               sunucu 200 + boş liste dönse bile ekran "Faturalar yüklenemedi." kırmızı
               uyarısını çiziyordu — hata yokken hata görünüyordu. */
            if (res.success) {
                setRows(res.data || []);
            } else {
                setError(res.error || 'Faturalar yüklenemedi.');
            }
        } catch {
            setError('Faturalar yüklenemedi.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const submitObjection = async () => {
        if (note.trim().length < 5) return;
        setSaving(true);

        try {
            const res = await apiService.post(
                `${API_ENDPOINTS.FLEET_PANEL.SOFTWARE_FEE_OBJECT(objecting.id)}?note=${encodeURIComponent(note.trim())}`,
            );

            if (res.success) {
                setObjecting(null);
                setNote('');
                load();
            } else {
                setError(res.error || 'İtiraz gönderilemedi.');
            }
        } catch {
            setError('İtiraz gönderilemedi.');
        } finally {
            setSaving(false);
        }
    };

    // En kötü aşama üstte tek uyarı olarak gösteriliyor: her satıra ayrı uyarı koymak
    // asıl mesajı boğardı.
    const worstStage = rows
        .map((r) => r.suspensionStage)
        .find((s) => STAGE_NOTICE[s]);

    return (
        <div className={`software-fee-page ${embedded ? 'is-embedded' : ''}`}>
            {!embedded && (
                <header className="sf-head">
                    <div>
                        <h1>Yazılım Bedeli</h1>
                        <p>Hızlıyo&apos;nun entegrasyon kanalı için kestiği aylık fatura.</p>
                    </div>
                    <button className="sf-refresh" onClick={load} disabled={loading}>
                        <RefreshCw size={16} className={loading ? 'sf-spin' : undefined} /> Yenile
                    </button>
                </header>
            )}

            {error && <div className="sf-error">{error}</div>}

            {worstStage && (
                <Notice
                    tone={STAGE_NOTICE[worstStage].tone === 'danger' ? 'danger' : 'warning'}
                    title={STAGE_NOTICE[worstStage].title}
                >
                    <p>{STAGE_NOTICE[worstStage].body}</p>
                </Notice>
            )}

            <Notice tone="info" title="Bu tutar neyin karşılığı?">
                <ul>
                    <li>
                        Entegrasyon kanalından gelen ve <strong>teslim edilen</strong> her paket için
                        yazılım kullanım bedeli. İptal ve başarısız teslimatlar ücretlendirilmez.
                    </li>
                    <li>
                        Adedin kaynağı Hızlıyo sistem kaydıdır. İtirazınızı{' '}
                        <strong>fatura kesilmeden önce</strong> yapmalısınız; süre dolduğunda adet kesinleşir.
                    </li>
                    <li>
                        Restorandan aldığınız paket ücretiyle ilgisi yoktur — o para sizinle restoran
                        arasındadır, Hızlıyo taraf değildir.
                    </li>
                </ul>
            </Notice>

            {loading ? (
                <p className="sf-empty">Yükleniyor…</p>
            ) : rows.length === 0 ? (
                <p className="sf-empty">Henüz yazılım bedeli faturası oluşmadı.</p>
            ) : (
                <div className="sf-list">
                    {rows.map((row) => {
                        const meta = STATUS_META[row.status] || { label: row.status, tone: 'draft', hint: '' };
                        const canObject = row.status === 'Draft';

                        return (
                            <article key={row.id} className={`sf-card sf-${meta.tone}`}>
                                <div className="sf-card-head">
                                    <div>
                                        <h3>{MONTHS[row.month - 1]} {row.year}</h3>
                                        <span className="sf-sub">
                                            {row.deliveredPackageCount} teslim edilen paket × {formatTL(row.feePerPackage)}
                                        </span>
                                    </div>
                                    <span className={`sf-status sf-status-${meta.tone}`}>{meta.label}</span>
                                </div>

                                <div className="sf-amounts">
                                    <div>
                                        <span className="sf-label">Ara toplam</span>
                                        <strong>{formatTL(row.subtotalAmount)}</strong>
                                    </div>
                                    <div>
                                        <span className="sf-label">KDV (%{row.vatRate})</span>
                                        <strong>{formatTL(row.vatAmount)}</strong>
                                    </div>
                                    <div className="sf-total">
                                        {/* Yön açıkça yazılıyor: "faturalanacak" ibaresi bu modelde
                                            hiç geçmemeli, geçerse muhasebe ters kayıt açar. */}
                                        <span className="sf-label">Ödenecek (sizden Hızlıyo'ya)</span>
                                        <strong>{formatTL(row.totalAmount)}</strong>
                                    </div>
                                </div>

                                <div className="sf-meta">
                                    {row.objectionDeadlineUtc && row.status === 'Draft' && (
                                        <span>İtiraz süresi: {formatDate(row.objectionDeadlineUtc)}</span>
                                    )}
                                    {row.dueDateUtc && <span>Vade: {formatDate(row.dueDateUtc)}</span>}
                                    {row.invoiceNumber && <span>Fatura no: {row.invoiceNumber}</span>}
                                    {row.overdueDays > 0 && (
                                        <span className="sf-overdue">
                                            <AlertTriangle size={13} /> {row.overdueDays} gün gecikme
                                        </span>
                                    )}
                                </div>

                                {row.objectionNote && (
                                    <p className="sf-objection">İtirazınız: {row.objectionNote}</p>
                                )}

                                <p className="sf-hint">{meta.hint}</p>

                                {canObject && (
                                    <button
                                        className="sf-object-btn"
                                        onClick={() => { setObjecting(row); setNote(''); }}
                                    >
                                        <MessageSquare size={15} /> Adete itiraz et
                                    </button>
                                )}
                            </article>
                        );
                    })}
                </div>
            )}

            {objecting && (
                <div className="sf-modal-overlay" onClick={() => setObjecting(null)}>
                    <div className="sf-modal" onClick={(e) => e.stopPropagation()}>
                        <h3>Adete itiraz</h3>
                        <p className="sf-modal-text">
                            {MONTHS[objecting.month - 1]} {objecting.year} dönemi için{' '}
                            <strong>{objecting.deliveredPackageCount} paket</strong> hesaplandı.
                            Farklı bir adet gördüyseniz gerekçesini yazın; adet incelemeye alınır.
                        </p>
                        <textarea
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="Örn: 12 Ağustos'ta iptal edilen 4 paket faturaya girmiş görünüyor."
                            rows={4}
                            maxLength={500}
                        />
                        <div className="sf-modal-actions">
                            <button className="sf-btn-ghost" onClick={() => setObjecting(null)}>Vazgeç</button>
                            <button
                                className="sf-btn-primary"
                                onClick={submitObjection}
                                disabled={saving || note.trim().length < 5}
                            >
                                {saving ? <Loader2 size={15} className="sf-spin" /> : null} İtirazı Gönder
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SoftwareFeePage;
